import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, CheckCheck } from 'lucide-react';
import { api } from '../lib/api';
import { cn, formatRelative } from '../lib/utils';
import type { Notification } from '../types';

export function NotificationBell() {
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const queryClient = useQueryClient();

    const { data: notifications = [] } = useQuery({
        queryKey: ['notifications'],
        queryFn: () => api.get<Notification[]>('/api/notifications'),
        refetchInterval: 60_000,
    });

    const unreadCount = notifications.filter((n) => !n.readAt).length;

    const markRead = useMutation({
        mutationFn: (ids?: string[]) =>
            api.post('/api/notifications/read', ids ? { ids } : { all: true }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['notifications'] });
            void queryClient.invalidateQueries({ queryKey: ['me'] });
        },
    });

    useEffect(() => {
        if (!open) return;

        const onClickOutside = (event: MouseEvent) => {
            if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
        };
        const onEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false);
        };

        document.addEventListener('mousedown', onClickOutside);
        document.addEventListener('keydown', onEscape);
        return () => {
            document.removeEventListener('mousedown', onClickOutside);
            document.removeEventListener('keydown', onEscape);
        };
    }, [open]);

    return (
        <div className="relative" ref={containerRef}>
            <button
                type="button"
                onClick={() => setOpen((value) => !value)}
                className="relative p-2 rounded-lg hover:bg-white/10 transition-colors"
                aria-label={
                    unreadCount > 0
                        ? `Notifications, ${unreadCount} non lue${unreadCount > 1 ? 's' : ''}`
                        : 'Notifications'
                }
                aria-expanded={open}
            >
                <Bell className="w-4 h-4 text-white/70" />
                {unreadCount > 0 && (
                    <span
                        className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-[var(--color-green)] text-[var(--color-navy)] text-[10px] font-bold flex items-center justify-center tabular-nums"
                        aria-hidden
                    >
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                )}
            </button>

            {open && (
                <div className="absolute right-0 mt-2 w-80 sm:w-96 card overflow-hidden animate-slideUp z-50">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--color-border)]">
                        <h2 className="text-sm font-semibold text-[var(--color-text)]">Notifications</h2>
                        {unreadCount > 0 && (
                            <button
                                type="button"
                                onClick={() => markRead.mutate(undefined)}
                                className="text-xs text-[var(--color-primary)] hover:underline inline-flex items-center gap-1"
                            >
                                <CheckCheck className="w-3 h-3" aria-hidden />
                                Tout marquer comme lu
                            </button>
                        )}
                    </div>

                    <div className="max-h-96 overflow-y-auto">
                        {notifications.length === 0 ? (
                            <p className="px-4 py-8 text-center text-sm text-[var(--color-text-muted)]">
                                Aucune notification
                            </p>
                        ) : (
                            <ul>
                                {notifications.map((notification) => {
                                    const content = (
                                        <>
                                            <div className="flex items-start gap-2">
                                                {!notification.readAt && (
                                                    <span
                                                        className="w-1.5 h-1.5 rounded-full bg-[var(--color-primary)] mt-1.5 shrink-0"
                                                        aria-hidden
                                                    />
                                                )}
                                                <div className={cn('min-w-0', notification.readAt && 'pl-3.5')}>
                                                    <p className="text-sm font-medium text-[var(--color-text)]">
                                                        {notification.title}
                                                    </p>
                                                    {notification.body && (
                                                        <p className="mt-0.5 text-xs text-[var(--color-text-secondary)] line-clamp-2">
                                                            {notification.body}
                                                        </p>
                                                    )}
                                                    <p className="mt-1 text-xs text-[var(--color-text-muted)]">
                                                        {formatRelative(notification.createdAt)}
                                                    </p>
                                                </div>
                                            </div>
                                        </>
                                    );

                                    return (
                                        <li
                                            key={notification.id}
                                            className="border-b border-[var(--color-border)] last:border-0"
                                        >
                                            {notification.link ? (
                                                <Link
                                                    to={notification.link}
                                                    onClick={() => {
                                                        if (!notification.readAt) {
                                                            markRead.mutate([notification.id]);
                                                        }
                                                        setOpen(false);
                                                    }}
                                                    className="block px-4 py-3 hover:bg-[var(--color-bg-hover)] transition-colors"
                                                >
                                                    {content}
                                                </Link>
                                            ) : (
                                                <div className="px-4 py-3">{content}</div>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
